/**
 * Please read: a note on `mode` vs `environment`:
 * - `mode` is specific to whether we are running in `development` with HMR, or `production` with a static build. It
 *  is automatically provided by `vite` and is passed into the config via `vite.config.ts`.
 * - `environment` is specific to the server we would like to connect to, and the options are `development`, `staging`,
 *  or `production`. This is a custom parameter that is applied by setting the `ENVIRONMENT` environment variable.
 *
 * Generally speaking, local development will be done with `mode=development` and `environment=development`, although
 * it is possible to connect to `staging` and `prod` environments while running in `development` mode.
 *
 * `staging` and `prod` deployments are configured with `mode=production` and `environment=staging` or `environment=prod`.
 *
 * Additionally, `mode=development` and `environment=mock` is used to load the browser mocks via `msw`.
 */
import {throttle} from 'es-toolkit';

import {extractCompanionScriptAutoInitParams} from '#/boot/companionScript';
import {
  buildPageDetailsBootToolPayload,
  installPageDetailsBootToolListeners,
} from '#/boot/tools/pageDetailsBootTool';
import {createDeferredPromiseManager} from '#/helpers/deferredPromiseManager';
import {BootMessageCourier} from '#/helpers/iframe/courier/bootMessageCourier';
import {BootMessageDataTypesEnum} from '#/helpers/iframe/courier/types';
import {
  type IframeLayoutMarginParams,
  type IframeLayoutMargins,
  applyIframeCssVariables,
  applyIframeHiddenStyles,
  applyIframeLayoutStyles,
  checkIsNarrowViewport,
  parseIframeLayoutMargins,
  parseLayoutPreset,
  releaseHostBodyScrollLock,
} from '#/helpers/iframe/layout';
import {formatIframeSrc, formatIframeSrcParams} from '#/helpers/iframe/src';
import type {ClientContext} from '#/models/clientContext';
import {decodeClientContext, decodeClientContextRemoveKeys} from '#/models/clientContext';
import type {ConversationEvent, FrontCompanionEvent} from '#/models/companionEvent';
import type {FeatureFlags} from '#/models/featureFlags';
import {
  type DeviceModesEnum,
  LayoutPresetsEnum,
  LayoutTypesEnum,
  ViewsEnum,
  WidgetLayoutPositionsEnum,
  decodeDeviceModesEnum,
  decodeLayoutPreset,
  defaultLayoutPreset,
} from '#/models/frame';
import type {SettingsState} from '#/models/froyoSettings';
import {type FroyoStyle, decodeFroyoStyle} from '#/models/froyoStyle';
import {HostPageHarnessParamKeysEnum} from '#/models/harness';
import {HomeModesEnum, decodeHomeMode} from '#/models/home';
import {LauncherModesEnum, decodeLauncherMode} from '#/models/launcher';
import type {Preferences} from '#/models/preferences';
import {checkIsObviousEnabled, checkIsProductionDeployment} from '#config/helpers';
import {getConfig} from '#config/index';

/*
 * Types.
 */

interface InitParams extends IframeLayoutMarginParams {
  container: HTMLElement;
  // When a cookie-consent banner gates the widget, the host passes `disabled: true` so that
  // `window.FrontCompanion` is available but the iframe is never created (no network, no cookies,
  // no UI). The host calls `enable(true)` once consent is granted to boot.
  disabled: boolean | undefined;
  companionToken: string | undefined;
  userJwt: string | undefined;
  layout: LayoutPresetsEnum | undefined;
  // Overrides the widget's own pointer/width mobile-detection heuristic. Intended for embedders
  // (e.g. settings previews) whose iframe is too narrow to reflect the real device.
  forceDeviceMode: DeviceModesEnum | undefined;
  style: FroyoStyle | undefined;
  launcher: LauncherModesEnum | undefined;
  // Where a fresh launch lands. Only `'overview'` changes anything, and only in the fullscreen layout.
  home: HomeModesEnum | undefined;
  locale: string | undefined;
  demo: boolean | undefined;
  skipVerification: boolean | undefined;
  featureFlags: Partial<FeatureFlags> | undefined;
  conversationId: string | undefined;
  accessToken: string | undefined;
  context?: ClientContext;
  onConversationChange?: (conversationId: string | undefined) => void;
  onConversationEvent?: (event: ConversationEvent) => void;
}

interface FrontCompanion {
  // We cannot trust that users will correctly pass in the parameters. To catch input errors,
  // we treat them as optional and explicitly validate that they are correctly set.
  init: (params: Partial<InitParams>) => Promise<void>;
  // Toggles the cookie-consent gate. `enable(true)` boots the widget using the params from the
  // most recent `init` call; `enable(false)` tears the widget down (e.g. consent withdrawn) while
  // keeping those params so a later `enable(true)` can re-boot without re-initializing.
  enable: (isEnabled: boolean) => Promise<void>;
  layout: (layoutPreset: LayoutPresetsEnum, marginParams?: IframeLayoutMarginParams) => Promise<void>;
  style: (style: FroyoStyle) => Promise<Record<string, unknown> | undefined>;
  settings: (settings: SettingsState) => Promise<void>;
  context: (context: unknown) => Promise<void>;
  setContext: (context: unknown) => Promise<void>;
  mergeContext: (context: unknown) => Promise<void>;
  removeContext: (keys: ReadonlyArray<string>) => Promise<void>;
  show: () => Promise<void>;
  hide: () => Promise<void>;
  shutdown: () => Promise<void>;
  featureFlags: (overrides: Partial<FeatureFlags>) => Promise<void>;
  obvious: (isEnabled: boolean) => Promise<void>;
  preferences: (preferences: Partial<Preferences>) => Promise<void>;
  // Synchronous, unlike every other method here — a promise-returning subscribe would defeat the
  // purpose: the host needs the unsubscribe handle immediately.
  on: (handler: (event: FrontCompanionEvent) => void) => () => void;
}

/*
 * Constants.
 */

const config = getConfig();

// If this is either a development mode (local build with HMR) or a production build with the `development` environment,
// we will set the `isDevelopment` flag to `true`.

/*
 * Boot.
 */

/**
 * Makes a boot function that handles aspects of the froyo lifecycle
 * that should be consistent across environments. For example:
 * - Managing a reference to the froyo iframe element.
 * - Attaching the `FrontCompanion` object to the `window` object.
 * - Initializing and shutting down the froyo app.
 *
 * DO NOT THROW ERRORS IN `window.FrontCompanion`!!!
 * We do not want `window.FrontCompanion` to surface errors that could impact completely
 * unrelated systems that belong to both ourselves and our customers.
 */
function makeBootMode() {
  return () => {
    // The froyo bundle will load properly in the cross-origin iframe created by the boot script,
    // but we cannot use `window.postMessage` if the page is served from the file:// protocol.
    if (window.location.protocol === 'file:') {
      console.warn(
        'FrontCompanion cannot run on the file:// protocol. Please serve this page from an https origin.',
      );
      return;
    }

    if (!window.location.origin || window.location.origin === 'null') {
      console.warn(
        'FrontCompanion cannot run with an invalid or null origin. Please serve this page from an https origin.',
      );
      return;
    }

    let iframe: HTMLIFrameElement | undefined;
    let courier: BootMessageCourier | undefined;
    let teardownHostPageDetailsListeners: (() => void) | undefined;
    let layoutPreset: LayoutPresetsEnum = defaultLayoutPreset;
    let view: ViewsEnum = ViewsEnum.LAUNCHER;
    let layoutMargins: IframeLayoutMargins = {};
    let froyoStyle: FroyoStyle | undefined;
    let froyoSettings: SettingsState | undefined;
    let hasForcedLayout = false;
    let launcherMode: LauncherModesEnum = LauncherModesEnum.DEFAULT;
    // Overrides the widget's own viewport-based mobile detection (both the React app's
    // `useIsMobile` and this boot script's own narrow-host-iframe collapsing below).
    let forceDeviceMode: DeviceModesEnum | undefined;
    // When a content-sized surface (e.g. a message preview) is shown at the launcher view, it asks
    // boot to grow the launcher-view iframe to fit. Undefined = default launcher frame size.
    let launcherSizeOverride: {width: number; height: number} | undefined;
    // The params from the most recent `init` call, retained so the cookie-consent gate can boot
    // (or re-boot) the widget via `enable(true)` without the host re-supplying them.
    let pendingInitParams: Partial<InitParams> | undefined;

    // Tracks the init-supplied `onConversationEvent` handler (as opposed to those registered via
    // `on()`, which own their own unsubscribe) so a superseded handler from a later `init` call
    // is replaced, not accumulated, across an `enable(false)` -> `enable(true)` replay.
    let initConversationEventListener: ((event: ConversationEvent) => void) | undefined;
    const conversationEventListeners = new Set<(event: ConversationEvent) => void>();

    // Fans an event out to every host listener. Each call is isolated: a throwing host callback
    // must not break the courier or starve the other listeners.
    const emitConversationEvent = (event: ConversationEvent) => {
      for (const listener of conversationEventListeners) {
        try {
          listener(event);
        } catch (error) {
          console.error('FrontCompanion conversation event listener error:', error);
        }
      }
    };

    // When launcher is `none`, the iframe is invisible at the launcher view (0×0, no pointer
    // events) so there is nothing for the user to interact with until show() is called.
    const applyIframeViewStylesOrHide = (
      targetIframe: HTMLIFrameElement,
      targetLayout: ReturnType<typeof parseLayoutPreset>,
      targetView: ViewsEnum,
      targetMargins: IframeLayoutMargins,
    ) => {
      if (launcherMode === LauncherModesEnum.NONE && targetView === ViewsEnum.LAUNCHER) {
        applyIframeHiddenStyles(targetIframe);
      } else {
        // `applyIframeLayoutStyles` only resets props in LAYOUT_CSS_PROPS, which excludes
        // `pointer-events` and `overflow`. Clear them explicitly so they don't persist
        // from the hidden state when the iframe becomes visible again.
        targetIframe.style.removeProperty('pointer-events');
        targetIframe.style.removeProperty('overflow');
        applyIframeLayoutStyles(
          targetIframe,
          targetLayout,
          targetView,
          targetMargins,
          launcherSizeOverride,
          forceDeviceMode,
        );
      }
    };

    const throttledWindowSizeHandler = throttle(() => {
      const needsFullscreenStyles = checkIsNarrowViewport(forceDeviceMode);
      const needsForcedWidgetLayout =
        [LayoutPresetsEnum.SIDEBAR_LEFT, LayoutPresetsEnum.SIDEBAR_RIGHT].includes(layoutPreset) &&
        needsFullscreenStyles;

      courier?.postMessage({
        type: BootMessageDataTypesEnum.PARENT_RESIZE,
        height: window.innerHeight,
        width: window.innerWidth,
      });

      if (!iframe) {
        return;
      }

      if (!needsFullscreenStyles) {
        applyIframeViewStylesOrHide(iframe, parseLayoutPreset(layoutPreset), view, layoutMargins);
        courier?.postMessage({
          type: BootMessageDataTypesEnum.LAYOUT,
          layoutPreset: layoutPreset,
        });
        return;
      }

      if (needsForcedWidgetLayout) {
        courier?.postMessage({
          type: BootMessageDataTypesEnum.LAYOUT,
          layoutPreset: LayoutPresetsEnum.WIDGET_RIGHT,
        });
        hasForcedLayout = true;
      } else if (hasForcedLayout) {
        courier?.postMessage({
          type: BootMessageDataTypesEnum.LAYOUT,
          layoutPreset: layoutPreset,
        });
      }

      applyIframeViewStylesOrHide(
        iframe,
        {type: LayoutTypesEnum.WIDGET, position: WidgetLayoutPositionsEnum.RIGHT},
        view,
        {},
      );
    }, 200);

    const deferredStyle = createDeferredPromiseManager<Record<string, unknown>>();

    const onStyle = (cssVariables: Record<string, unknown>) => {
      deferredStyle.resolveWith(cssVariables);
      if (iframe) {
        applyIframeCssVariables(iframe, cssVariables);
      }
    };

    // Tears down the iframe and its listeners. Shared by `shutdown` (full teardown) and
    // `enable(false)` (consent withdrawn), which keeps `pendingInitParams` so the widget can
    // be re-booted later.
    const teardownIframe = () => {
      deferredStyle.cancel(new Error('FrontCompanion teardown'));

      teardownHostPageDetailsListeners?.();
      teardownHostPageDetailsListeners = undefined;
      window.removeEventListener('resize', throttledWindowSizeHandler);

      // Tearing down from the window view on a narrow viewport would otherwise leave the host page
      // pinned by the scroll lock with nothing left to unpin it.
      if (iframe) {
        releaseHostBodyScrollLock(iframe);
      }

      iframe?.remove();
      iframe = undefined;
      launcherSizeOverride = undefined;

      courier?.shutdown();
      courier = undefined;
    };

    // Creates the cross-origin iframe and wires up the courier handshake. This is the actual
    // "boot": it is the first point at which any network request or cookie can occur, so it is
    // only reached once the cookie-consent gate (`disabled` / `enable`) allows it.
    const bootIframe = (params: Partial<InitParams>): Promise<void> | undefined => {
      // Feature flag overrides are not available in production builds, but they are
      // available in local development, connected to production.
      if (params.featureFlags && checkIsProductionDeployment(config)) {
        console.warn('FrontCompanion init error: featureFlags not available');
        return;
      }

      if (!params.container) {
        console.warn('init requires a container');
        return;
      }

      if (params.onConversationEvent) {
        if (initConversationEventListener) {
          conversationEventListeners.delete(initConversationEventListener);
        }
        initConversationEventListener = params.onConversationEvent;
        conversationEventListeners.add(params.onConversationEvent);
      }
      const container = params.container;

      let clientContext: ClientContext | undefined;
      if (params.context !== undefined) {
        try {
          clientContext = decodeClientContext(params.context);
        } catch (error) {
          console.warn('FrontCompanion.init: invalid context', error);
        }
      }

      iframe = document.createElement('iframe');

      const iframeSrc = formatIframeSrc(
        config.bundleOrigin,
        config.environment,
        config.namespace,
        config.mode,
      );

      const iframeOrigin = new URL(iframeSrc).origin;

      const companionToken: string = params.companionToken ?? '';
      if (params.layout) {
        layoutPreset = decodeLayoutPreset(params.layout);
      }

      if (params.launcher !== undefined) {
        try {
          launcherMode = decodeLauncherMode(params.launcher);
        } catch (error) {
          console.warn('FrontCompanion.init: invalid launcher', error);
        }
      }

      let homeMode = HomeModesEnum.DEFAULT;
      if (params.home !== undefined) {
        try {
          homeMode = decodeHomeMode(params.home);
        } catch (error) {
          console.warn('FrontCompanion.init: invalid home', error);
        }
      }

      if (params.forceDeviceMode !== undefined) {
        try {
          forceDeviceMode = decodeDeviceModesEnum(params.forceDeviceMode);
        } catch (error) {
          console.warn('FrontCompanion.init: invalid forceDeviceMode', error);
        }
      }

      layoutMargins = parseIframeLayoutMargins({
        sideMargin: params.sideMargin,
        bottomMargin: params.bottomMargin,
      });

      if (params.style) {
        try {
          froyoStyle = decodeFroyoStyle(params.style);
        } catch (error) {
          console.warn('FrontCompanion.init: invalid style', error);
        }
      }

      // TODO(greg 2025-11-14): `locale` is just for mock purposes. We will remove this.
      const locale = params.locale ?? 'en';

      const hostPageSearchParams = new URLSearchParams(window.location.search);

      iframe.src = formatIframeSrcParams({
        iframeSrc,
        companionToken,
        userJwt: params.userJwt,
        layoutPreset,
        view,
        launcher: launcherMode,
        home: homeMode,
        locale,
        isDemo: Boolean(params.demo),
        skipVerification: Boolean(params.skipVerification),
        conversationId: params.conversationId,
        accessToken: params.accessToken,
        forceDeviceMode,
        harness: hostPageSearchParams.get(HostPageHarnessParamKeysEnum.HARNESS) ?? undefined,
        harnessExperiments:
          hostPageSearchParams.get(HostPageHarnessParamKeysEnum.HARNESS_EXPERIMENTS) ?? undefined,
      });

      // Send the full URL as the referrer, unless navigating to an insecure (HTTP) site.
      iframe.referrerPolicy = 'no-referrer-when-downgrade';

      applyIframeViewStylesOrHide(iframe, parseLayoutPreset(layoutPreset), view, layoutMargins);

      // The init promise is returned immediately and settles after the iframe `postMessage` handshake completes.
      const deferredInit = createDeferredPromiseManager<void>();
      const initPromise = new Promise<void>(deferredInit.bindPending);

      iframe.onload = () => {
        const postPageDetailsToIframe = () => {
          if (!courier) {
            return;
          }
          courier.postMessage(buildPageDetailsBootToolPayload());
        };

        const onReady = () => {
          if (froyoStyle) {
            courier?.postMessage({type: BootMessageDataTypesEnum.STYLE, style: froyoStyle});
          }
          if (froyoSettings) {
            courier?.postMessage({type: BootMessageDataTypesEnum.SETTINGS, settings: froyoSettings});
          }
          if (params.featureFlags) {
            courier?.postMessage({
              type: BootMessageDataTypesEnum.FEATURE_FLAGS,
              overrides: params.featureFlags,
            });
          }
          if (clientContext) {
            courier?.postMessage({
              type: BootMessageDataTypesEnum.CONTEXT,
              clientContext,
            });
          }

          postPageDetailsToIframe();
          if (!teardownHostPageDetailsListeners) {
            teardownHostPageDetailsListeners = installPageDetailsBootToolListeners(postPageDetailsToIframe);
          }

          deferredInit.resolveWith(undefined);

          window.addEventListener('resize', throttledWindowSizeHandler);
          throttledWindowSizeHandler();
        };

        const applyIframeViewStyles = (newView: ViewsEnum) => {
          if (newView === view) {
            return;
          }

          // iframe should always exist as this callback is triggered by an iframe event.
          applyIframeViewStylesOrHide(iframe!, parseLayoutPreset(layoutPreset), newView, layoutMargins);

          // froyo holds off on rendering until the view is synced.
          courier?.postMessage({type: BootMessageDataTypesEnum.VIEW, view: newView});

          view = newView;
        };

        const applyContentSizeOverride = (size: {width: number; height: number} | undefined) => {
          launcherSizeOverride = size;
          if (iframe && view === ViewsEnum.LAUNCHER) {
            // Resize instantly (no transition): the iframe is transparent around the surface, so the
            // frame size change isn't visible on its own — only the surface's entrance is — and this
            // avoids reflowing the content mid-animation.
            applyIframeViewStylesOrHide(iframe, parseLayoutPreset(layoutPreset), view, layoutMargins);
          }
        };

        courier = new BootMessageCourier(
          window,
          iframe!,
          iframe!.contentWindow!,
          iframeOrigin,
          onReady,
          onStyle,
          applyIframeViewStyles,
          params.onConversationChange,
          emitConversationEvent,
          applyContentSizeOverride,
        );
      };

      container.appendChild(iframe);

      return initPromise;
    };

    const frontCompanion: FrontCompanion = {
      init: async (params) => {
        try {
          // Merge with any params retained from a previous `init` so the host can re-init with
          // only the fields that changed (notably flipping `disabled`) without re-supplying the
          // container, token, etc.
          const mergedParams = pendingInitParams ? {...pendingInitParams, ...params} : params;
          pendingInitParams = mergedParams;

          if (!mergedParams.container) {
            console.warn('init requires a container');
            return;
          }

          if (iframe) {
            console.warn('FrontCompanion is already initialized');
            return;
          }

          // Cookie-consent gate: when consent is required but not yet granted, the host passes
          // `disabled: true`. We keep `window.FrontCompanion` available and retain the params, but
          // skip iframe creation so no network requests or cookies occur. The host calls
          // `enable(true)` once consent is granted to boot.
          if (mergedParams.disabled) {
            return;
          }

          return await bootIframe(mergedParams);
        } catch (error) {
          console.error('FrontCompanion.init error:', error);
        }
      },
      enable: async (isEnabled) => {
        try {
          if (isEnabled) {
            if (iframe) {
              // Already booted; nothing to do.
              return;
            }
            if (!pendingInitParams?.container) {
              console.warn('FrontCompanion.enable: call init before enabling');
              return;
            }
            return await bootIframe(pendingInitParams);
          }

          // Consent withdrawn: tear the widget down but keep `pendingInitParams` so a subsequent
          // `enable(true)` can re-boot without re-initializing.
          if (!iframe) {
            return;
          }
          teardownIframe();
        } catch (error) {
          console.error('FrontCompanion.enable error:', error);
        }
      },
      show: async () => {
        try {
          if (!iframe) {
            console.warn('FrontCompanion is not initialized');
            return;
          }
          courier?.postMessage({type: BootMessageDataTypesEnum.SHOW});
        } catch (error) {
          console.error('FrontCompanion.show error:', error);
        }
      },
      hide: async () => {
        try {
          if (!iframe) {
            console.warn('FrontCompanion is not initialized');
            return;
          }
          courier?.postMessage({type: BootMessageDataTypesEnum.HIDE});
        } catch (error) {
          console.error('FrontCompanion.hide error:', error);
        }
      },
      shutdown: async () => {
        try {
          if (!iframe) {
            console.warn('FrontCompanion is not initialized');
            return;
          }

          teardownIframe();
          // A full shutdown also forgets the retained init params; booting again requires a fresh
          // `init` call (unlike `enable(false)`, which keeps them).
          pendingInitParams = undefined;
          conversationEventListeners.clear();
          initConversationEventListener = undefined;
        } catch (error) {
          console.error('FrontCompanion.shutdown error:', error);
        }
      },
      context: async (raw) => {
        try {
          const decoded = decodeClientContext(raw);
          courier?.postMessage({
            type: BootMessageDataTypesEnum.CONTEXT,
            clientContext: decoded,
          });
        } catch (error) {
          console.warn('FrontCompanion.context: invalid context', error);
        }
      },
      setContext: async (raw) => {
        try {
          const decoded = decodeClientContext(raw);
          courier?.postMessage({
            type: BootMessageDataTypesEnum.CONTEXT,
            clientContext: decoded,
          });
        } catch (error) {
          console.warn('FrontCompanion.setContext: invalid context', error);
        }
      },
      mergeContext: async (raw) => {
        try {
          const decoded = decodeClientContext(raw);
          courier?.postMessage({
            type: BootMessageDataTypesEnum.CONTEXT_MERGE,
            clientContext: decoded,
          });
        } catch (error) {
          console.warn('FrontCompanion.mergeContext: invalid context', error);
        }
      },
      removeContext: async (raw) => {
        try {
          const decoded = decodeClientContextRemoveKeys(raw);
          courier?.postMessage({
            type: BootMessageDataTypesEnum.CONTEXT_REMOVE,
            keys: decoded,
          });
        } catch (error) {
          console.warn('FrontCompanion.removeContext: invalid keys', error);
        }
      },
      layout: async (newLayoutPreset, marginParams) => {
        try {
          if (!iframe) {
            console.warn('FrontCompanion is not initialized');
            return;
          }

          if (marginParams) {
            layoutMargins = parseIframeLayoutMargins(marginParams);
          }

          applyIframeLayoutStyles(
            iframe,
            parseLayoutPreset(newLayoutPreset),
            view,
            layoutMargins,
            launcherSizeOverride,
            forceDeviceMode,
          );

          layoutPreset = newLayoutPreset;

          courier?.postMessage({
            type: BootMessageDataTypesEnum.LAYOUT,
            layoutPreset: newLayoutPreset,
          });
        } catch (error) {
          console.error('FrontCompanion.layout error:', error);
        }
      },
      style: async (style) => {
        try {
          if (!iframe) {
            console.warn('FrontCompanion is not initialized');
            return;
          }

          let decodedStyle: FroyoStyle;
          try {
            decodedStyle = decodeFroyoStyle(style);
          } catch (error) {
            console.warn('FrontCompanion.style: invalid style', error);
            return;
          }

          froyoStyle = decodedStyle;

          deferredStyle.cancel(new Error('FrontCompanion.style superseded by a newer call'));

          courier?.postMessage({
            type: BootMessageDataTypesEnum.STYLE,
            style: decodedStyle,
          });

          // The style promise is returned immediately and settles when the style is applied within the iframe.
          return await new Promise<Record<string, unknown>>(deferredStyle.bindPending);
        } catch (error) {
          console.error('FrontCompanion.style error:', error);
        }
      },
      settings: async (settings) => {
        try {
          if (!iframe) {
            console.warn('FrontCompanion is not initialized');
            return;
          }

          froyoSettings = settings;

          courier?.postMessage({
            type: BootMessageDataTypesEnum.SETTINGS,
            settings,
          });
        } catch (error) {
          console.error('FrontCompanion.settings error:', error);
        }
      },
      featureFlags: async (overrides) => {
        try {
          if (!iframe) {
            console.warn('FrontCompanion is not initialized');
            return;
          }
          if (checkIsProductionDeployment(config)) {
            return;
          }
          courier?.postMessage({type: BootMessageDataTypesEnum.FEATURE_FLAGS, overrides});
        } catch (error) {
          console.error('FrontCompanion.featureFlags error:', error);
        }
      },
      obvious: async (isEnabled) => {
        try {
          if (!checkIsObviousEnabled(config)) {
            return;
          }
          if (!iframe) {
            console.warn('FrontCompanion is not initialized');
            return;
          }
          courier?.postMessage({type: BootMessageDataTypesEnum.OBVIOUS, isEnabled});
        } catch (error) {
          console.error('FrontCompanion.obvious error:', error);
        }
      },
      preferences: async (preferences) => {
        try {
          if (!iframe) {
            console.warn('FrontCompanion is not initialized');
            return;
          }
          if (checkIsProductionDeployment(config)) {
            return;
          }
          courier?.postMessage({type: BootMessageDataTypesEnum.PREFERENCES, preferences});
        } catch (error) {
          console.error('FrontCompanion.preferences error:', error);
        }
      },
      on: (handler) => {
        try {
          if (typeof handler !== 'function') {
            console.warn('FrontCompanion.on: handler must be a function');
            return () => {};
          }
          conversationEventListeners.add(handler);
          return () => {
            conversationEventListeners.delete(handler);
          };
        } catch (error) {
          console.error('FrontCompanion.on error:', error);
          return () => {};
        }
      },
    };

    window.FrontCompanion = frontCompanion;

    const autoInitParams = extractCompanionScriptAutoInitParams();
    if (!autoInitParams) {
      return;
    }

    layoutPreset = autoInitParams.layout ?? layoutPreset;

    void frontCompanion.init({
      container: autoInitParams.container,
      companionToken: autoInitParams.companionToken,
      userJwt: autoInitParams.userJwt,
      layout: autoInitParams.layout,
      sideMargin: autoInitParams.sideMargin,
      bottomMargin: autoInitParams.bottomMargin,
      launcher: autoInitParams.launcherMode,
      home: autoInitParams.homeMode,
      disabled: autoInitParams.disabled,
      demo: autoInitParams.demo,
      conversationId: autoInitParams.conversationId,
      accessToken: autoInitParams.accessToken,
      onConversationChange: autoInitParams.onConversationChange,
      onConversationEvent: autoInitParams.onConversationEvent,
    });
  };
}

/*
 * Entry points.
 */

const standardBoot = makeBootMode();

standardBoot();
